import React, { useState } from 'react';
import { Folder, Plus, Search, X } from 'lucide-react';
import { useFolders } from '../../contexts/FolderContext';
import { Folder as FolderType } from '../../types/shared';

interface FolderSelectorProps {
  selectedFolderId?: string | null;
  onFolderSelect: (folder: FolderType | null) => void;
  onClose?: () => void;
  title?: string;
  showSearch?: boolean;
  className?: string;
}

interface FolderOptionProps {
  folder: FolderType & { children?: FolderType[] };
  level: number;
  selectedFolderId?: string | null;
  onFolderSelect: (folder: FolderType | null) => void;
  searchTerm?: string;
}

const FolderOption: React.FC<FolderOptionProps> = ({
  folder,
  level,
  selectedFolderId,
  onFolderSelect,
  searchTerm
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedFolderId === folder._id;
  
  // Filter children based on search term
  const filteredChildren = searchTerm 
    ? folder.children?.filter(child => 
        child.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        child.children?.some(grandchild => 
          grandchild.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : folder.children;

  const handleClick = () => {
    if (hasChildren && !searchTerm) {
      setIsExpanded(!isExpanded);
    }
    onFolderSelect(folder);
  };

  const shouldShow = !searchTerm || 
    folder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    filteredChildren && filteredChildren.length > 0;

  if (!shouldShow) return null;

  return (
    <div>
      <div
        className={`
          flex items-center py-2 px-3 cursor-pointer hover:bg-gray-700 rounded-md
          ${isSelected ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300'}
        `}
        style={{ paddingLeft: `${level * 20 + 12}px` }}
        onClick={handleClick}
      >
        {/* Expand/Collapse Icon */}
        <div className="w-4 h-4 mr-2 flex items-center justify-center">
          {hasChildren && !searchTerm ? (
            isExpanded ? (
              <div className="w-2 h-2 bg-gray-400 rounded-full" />
            ) : (
              <div className="w-2 h-2 bg-gray-400 rounded-full" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </div>

        {/* Folder Icon */}
        <div
          className="w-4 h-4 mr-2 flex items-center justify-center"
          style={{ color: folder.color }}
        >
          <Folder className="w-4 h-4" />
        </div>

        {/* Folder Name */}
        <span className="text-sm font-medium flex-1">{folder.name}</span>

        {/* Ads Count */}
        <span className="text-xs text-gray-400 ml-2">
          {folder.adsCount || 0}
        </span>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && filteredChildren && (
        <div>
          {filteredChildren.map((child) => (
            <FolderOption
              key={child._id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              onFolderSelect={onFolderSelect}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const FolderSelector: React.FC<FolderSelectorProps> = ({
  selectedFolderId,
  onFolderSelect,
  onClose,
  title = "Seleccionar Carpeta",
  showSearch = true,
  className = ''
}) => {
  const { foldersTree, isLoading, error, createFolder } = useFolders();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      try {
        await createFolder(newFolderName.trim(), selectedParentId);
        setNewFolderName('');
        setShowCreateForm(false);
        setSelectedParentId(null);
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      setShowCreateForm(false);
      setNewFolderName('');
      setSelectedParentId(null);
    }
  };

  const filteredFolders = searchTerm
    ? foldersTree.filter(folder =>
        folder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        folder.children?.some(child =>
          child.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : foldersTree;

  if (isLoading) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 text-red-600 ${className}`}>
        <p className="text-sm">Error al cargar carpetas: {error}</p>
      </div>
    );
  }

  return (
    <div className={className || `bg-gray-800 rounded-lg border border-gray-700`}>
      {/* Header - Only show if not embedded in modal */}
      {!className?.includes('bg-transparent') && (
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-700 rounded"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          )}
        </div>
      )}

      {/* Search */}
      {showSearch && (
        <div className={`p-4 ${!className?.includes('bg-transparent') ? 'border-b border-gray-700' : ''}`}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar carpetas..."
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Create Folder Button */}
      <div className={`p-4 ${!className?.includes('bg-transparent') ? 'border-b border-gray-700' : ''}`}>
        <button
          onClick={() => setShowCreateForm(true)}
          className="w-full flex items-center justify-center px-4 py-2 border border-gray-600 rounded-lg hover:bg-gray-700 text-gray-300 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Carpeta
        </button>
      </div>

      {/* Create Folder Form */}
      {showCreateForm && (
        <div className="p-4 border-b border-gray-700 bg-gray-800/50">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Nombre de la carpeta
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ej: Marketing Digital"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Carpeta padre (opcional)
              </label>
              <select
                value={selectedParentId || ''}
                onChange={(e) => setSelectedParentId(e.target.value || null)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Carpeta raíz</option>
                {foldersTree.map((folder) => (
                  <option key={folder._id} value={folder._id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCreateFolder}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Crear
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewFolderName('');
                  setSelectedParentId(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder List */}
      <div className="max-h-96 overflow-y-auto">
        {/* "TODOS" option */}
        <div
          className={`
            flex items-center py-2 px-3 cursor-pointer hover:bg-gray-700 rounded-md
            ${selectedFolderId === null ? 'bg-blue-500/20 text-blue-400' : 'text-gray-300'}
          `}
          onClick={() => onFolderSelect(null)}
        >
          <div className="w-4 h-4 mr-2 flex items-center justify-center">
            <Folder className="w-4 h-4 text-gray-400" />
          </div>
          <span className="text-sm font-medium">TODOS</span>
        </div>

        {/* Folder Tree */}
        {filteredFolders.map((folder) => (
          <FolderOption
            key={folder._id}
            folder={folder}
            level={0}
            selectedFolderId={selectedFolderId}
            onFolderSelect={onFolderSelect}
            searchTerm={searchTerm}
          />
        ))}

        {filteredFolders.length === 0 && searchTerm && (
          <div className="p-4 text-center text-gray-400">
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No se encontraron carpetas</p>
            <p className="text-xs text-gray-500">Intenta con otro término de búsqueda</p>
          </div>
        )}

        {filteredFolders.length === 0 && !searchTerm && (
          <div className="p-4 text-center text-gray-400">
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay carpetas creadas</p>
            <p className="text-xs text-gray-500">Crea tu primera carpeta para organizar tus anuncios</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderSelector;

